/**
 * Browsers often ignore non-standard MP4 track transforms (e.g. vertical flip from iOS KoT capture).
 * Parse the video track matrix and return a CSS transform when the element reports un-oriented dimensions.
 */

import { ip_back } from "../config";

const FETCH_BYTES = 256 * 1024;

function readBoxType(view, offset) {
  return String.fromCharCode(
    view.getUint8(offset),
    view.getUint8(offset + 1),
    view.getUint8(offset + 2),
    view.getUint8(offset + 3)
  );
}

function readBoxHeader(view, offset) {
  const size32 = view.getUint32(offset);
  if (size32 === 1) {
    return {
      size: Number(view.getBigUint64(offset + 8)),
      header: 16,
    };
  }
  return { size: size32, header: 8 };
}

function findBoxRecursive(view, type, start, end) {
  let offset = start;
  while (offset + 8 <= end) {
    const { size, header } = readBoxHeader(view, offset);
    if (size < header || offset + size > end) break;
    const boxType = readBoxType(view, offset + 4);
    const contentStart = offset + header;
    const boxEnd = offset + size;
    if (boxType === type) {
      return { start: offset, end: boxEnd, header };
    }
    const nested = findBoxRecursive(view, type, contentStart, boxEnd);
    if (nested) return nested;
    offset = boxEnd;
  }
  return null;
}

function readFixed16(view, offset) {
  return view.getInt32(offset) / 65536;
}

function parseVideoTrackMatrix(buffer) {
  const view = new DataView(buffer);
  const moov = findBoxRecursive(view, "moov", 0, buffer.byteLength);
  if (!moov) return null;

  let offset = moov.start + moov.header;
  while (offset + 8 <= moov.end) {
    const { size, header } = readBoxHeader(view, offset);
    if (size < header || offset + size > moov.end) break;
    const boxType = readBoxType(view, offset + 4);
    const boxEnd = offset + size;
    if (boxType === "trak") {
      const tkhd = findBoxRecursive(view, "tkhd", offset + header, boxEnd);
      if (!tkhd) {
        offset = boxEnd;
        continue;
      }
      const base = tkhd.start + tkhd.header;
      const version = view.getUint8(base);
      const matrixOffset = version === 0 ? base + 40 : base + 52;
      const widthOffset = version === 0 ? base + 76 : base + 88;
      const heightOffset = version === 0 ? base + 80 : base + 92;
      if (heightOffset + 4 > tkhd.end) return null;

      const matrix = {
        a: readFixed16(view, matrixOffset),
        b: readFixed16(view, matrixOffset + 4),
        c: readFixed16(view, matrixOffset + 12),
        d: readFixed16(view, matrixOffset + 16),
      };
      const trackWidth = readFixed16(view, widthOffset);
      const trackHeight = readFixed16(view, heightOffset);
      if (trackWidth > 0 && trackHeight > 0) {
        return { matrix, trackWidth, trackHeight };
      }
    }
    offset = boxEnd;
  }
  return null;
}

function orientedSize(matrix, width, height) {
  const corners = [
    [0, 0],
    [width, 0],
    [0, height],
    [width, height],
  ].map(([x, y]) => ({
    x: matrix.a * x + matrix.c * y,
    y: matrix.b * x + matrix.d * y,
  }));
  const xs = corners.map((p) => p.x);
  const ys = corners.map((p) => p.y);
  return {
    width: Math.round(Math.max(...xs) - Math.min(...xs)),
    height: Math.round(Math.max(...ys) - Math.min(...ys)),
  };
}

function isIdentityMatrix(matrix) {
  const eps = 0.01;
  return (
    Math.abs(matrix.a - 1) < eps &&
    Math.abs(matrix.b) < eps &&
    Math.abs(matrix.c) < eps &&
    Math.abs(matrix.d - 1) < eps
  );
}

/**
 * CSS transform to apply when the browser left pixels in storage orientation.
 */
export function cssTransformForVideoMatrix(matrix, videoWidth, videoHeight) {
  if (!matrix || !videoWidth || !videoHeight || isIdentityMatrix(matrix)) {
    return "";
  }

  const naturalW = Math.round(matrix.trackWidth || videoWidth);
  const naturalH = Math.round(matrix.trackHeight || videoHeight);
  const display = orientedSize(matrix, naturalW, naturalH);

  const browserUsesNatural =
    videoWidth === naturalW && videoHeight === naturalH;
  const browserUsesDisplay =
    videoWidth === display.width && videoHeight === display.height;
  const dimensionsSwap =
    display.width !== naturalW || display.height !== naturalH;

  if (dimensionsSwap) {
    if (browserUsesDisplay) return "";
    if (!browserUsesNatural) return "";
  } else if (!browserUsesNatural) {
    return "";
  }

  const { a, b, c, d } = matrix;
  const eps = 0.1;

  if (Math.abs(a - 1) < eps && Math.abs(d + 1) < eps) {
    return "scaleY(-1)";
  }
  if (Math.abs(a + 1) < eps && Math.abs(d + 1) < eps) {
    return "rotate(180deg)";
  }
  if (Math.abs(b - 1) < eps && Math.abs(c + 1) < eps) {
    return "rotate(90deg)";
  }
  if (Math.abs(b + 1) < eps && Math.abs(c - 1) < eps) {
    return "rotate(-90deg)";
  }
  if (Math.abs(a + 1) < eps && Math.abs(d - 1) < eps) {
    return "scaleX(-1)";
  }

  return "";
}

/**
 * Normalize rotation to 0 | 90 | 180 | 270 (clockwise).
 */
export function normalizeRotationDeg(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return 0;
  const mod = ((Math.round(n) % 360) + 360) % 360;
  if (mod === 90 || mod === 180 || mod === 270) return mod;
  return 0;
}

/**
 * Build a CSS transform for coach/admin manual orientation edits.
 * CSS applies right-to-left; order is chosen so flips run before rotation,
 * matching server ffmpeg (`hflip`/`vflip` then `transpose`).
 */
export function cssTransformForUserOrientation({
  rotationDeg = 0,
  flipH = false,
  flipV = false,
} = {}) {
  const rotation = normalizeRotationDeg(rotationDeg);
  const parts = [];
  if (rotation) parts.push(`rotate(${rotation}deg)`);
  if (flipH) parts.push("scaleX(-1)");
  if (flipV) parts.push("scaleY(-1)");
  return parts.join(" ");
}

/**
 * Combine metadata auto-fix with user rotate/flip for live preview.
 * Metadata is applied first (rightmost), then the coach/admin edits.
 */
export function composeVideoCssTransform(
  metadataTransform,
  { rotationDeg = 0, flipH = false, flipV = false } = {}
) {
  const user = cssTransformForUserOrientation({ rotationDeg, flipH, flipV });
  return [user, metadataTransform].filter(Boolean).join(" ").trim();
}

export function hasUserVideoTransform({
  rotationDeg = 0,
  flipH = false,
  flipV = false,
} = {}) {
  return (
    normalizeRotationDeg(rotationDeg) !== 0 || Boolean(flipH) || Boolean(flipV)
  );
}

export async function fetchVideoTrackMatrix(url, { timeoutMs = 2500 } = {}) {
  if (!url || typeof fetch !== "function") return null;

  const tryParse = async (buffer) => {
    if (!buffer || !buffer.byteLength) return null;
    const parsed = parseVideoTrackMatrix(buffer);
    if (!parsed) return null;
    return {
      ...parsed.matrix,
      trackWidth: parsed.trackWidth,
      trackHeight: parsed.trackHeight,
    };
  };

  const withTimeout = (promise) => {
    if (!Number.isFinite(timeoutMs) || timeoutMs <= 0) return promise;
    return Promise.race([
      promise,
      new Promise((resolve) => {
        window.setTimeout(() => resolve(null), timeoutMs);
      }),
    ]);
  };

  const isRemoteS3 =
    /ttdata\.s3\.|s3\.gra\.io\.cloud\.ovh\.net/i.test(String(url));

  // S3 buckets are not CORS-enabled for browser Range fetches — use backend proxy.
  if (isRemoteS3) {
    return withTimeout(
      (async () => {
        try {
          const token =
            (typeof window !== "undefined" && localStorage.getItem("token")) ||
            "";
          const controller =
            typeof AbortController !== "undefined"
              ? new AbortController()
              : null;
          const abortTimer = controller
            ? window.setTimeout(() => controller.abort(), timeoutMs)
            : null;
          try {
            const response = await fetch(
              `${ip_back}api/mobileAPI/videoHeaderBytes`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  ...(token ? { Authorization: token } : {}),
                },
                body: JSON.stringify({ url }),
                signal: controller?.signal,
              }
            );
            if (!response.ok) return null;
            const json = await response.json().catch(() => ({}));
            const b64 = json?.data?.bytes_base64;
            if (!b64) return null;
            const binary = atob(b64);
            const bytes = new Uint8Array(binary.length);
            for (let i = 0; i < binary.length; i += 1) {
              bytes[i] = binary.charCodeAt(i);
            }
            return tryParse(bytes.buffer);
          } finally {
            if (abortTimer != null) window.clearTimeout(abortTimer);
          }
        } catch {
          return null;
        }
      })()
    );
  }

  return withTimeout(
    (async () => {
      try {
        const controller =
          typeof AbortController !== "undefined" ? new AbortController() : null;
        const abortTimer = controller
          ? window.setTimeout(() => controller.abort(), timeoutMs)
          : null;
        try {
          const response = await fetch(url, {
            headers: { Range: `bytes=0-${FETCH_BYTES - 1}` },
            credentials: "omit",
            signal: controller?.signal,
          });
          if (!response.ok && response.status !== 206) return null;
          const buffer = await response.arrayBuffer();
          return tryParse(buffer);
        } finally {
          if (abortTimer != null) window.clearTimeout(abortTimer);
        }
      } catch {
        return null;
      }
    })()
  );
}

export async function resolveVideoCssTransform(
  url,
  videoWidth,
  videoHeight,
  { timeoutMs = 1500 } = {}
) {
  const matrix = await fetchVideoTrackMatrix(url, { timeoutMs });
  if (!matrix) return "";
  return cssTransformForVideoMatrix(matrix, videoWidth, videoHeight);
}
